import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HobbyEmptyState from '../../components/common/HobbyEmptyState';
import RatingReadout from '../../components/common/RatingReadout';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ADD_BOOK,
  DELETE_BOOK,
  GET_BOOK,
  GET_BOOKS,
  IMPORT_GOODREADS_BOOKS,
  PREVIEW_GOODREADS_IMPORT,
  SEARCH_BOOKS,
  UPDATE_BOOK,
} from './operations';
import GoodreadsImportModal from './GoodreadsImportModal';
import ReadingAddSearch from './ReadingAddSearch';
import ReadingForm from './ReadingForm';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import DefaultMediaFallback from '../../components/common/DefaultMediaFallback';
import ModuleListSkeleton from '../../components/common/ModuleListSkeleton';
import DetailActionMenu from '../../components/common/DetailActionMenu';
import { GET_MY_SUBSCRIPTION_STATUS } from '../../views/account/operations';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { canCreateEntries, getHobbyUsageLimit, getRemainingEntries, hasPremiumForHobby } from '../../utils/subscriptionLimits';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { shareHighlightStrings } from '../../content/shareHighlights';
import { buildShareHighlight, openShareHighlight } from '../shareHighlights/shareHighlight';

const BOOKS_PAGE_SIZE = 20;

const defaultBook = {
  subtitle: '',
  authors: [],
  description: '',
  coverUrl: '',
  publishedYear: '',
  pageCount: 0,
  genres: [],
  shelf: 'Want to read',
  collection: 'My Books',
  format: 'Physical',
  favorite: false,
  rating: '',
  review: '',
  notes: '',
  progressType: 'Pages',
  currentPage: 0,
  currentPercent: 0,
  currentMinutes: 0,
  startedDate: '',
  finishedDate: '',
  lastReadDate: '',
};

/**
 * Performs the split csv operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function splitCsv(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

/**
 * Normalizes book.
 *
 * @param {*} book - book value.
 * @returns {*} The derived operation result.
 */
function parseBook(book) {
  return {
    ...defaultBook,
    ...book,
    authors: Array.isArray(book.authors) ? book.authors : splitCsv(book.author || book.authors),
    genres: Array.isArray(book.genres) ? book.genres : splitCsv(book.genres),
    shelf: book.shelf || book.status || defaultBook.shelf,
    collection: book.collection || defaultBook.collection,
    format: book.format || defaultBook.format,
    favorite: Boolean(book.favorite),
    pageCount: Number(book.pageCount || 0),
    currentPage: Number(book.currentPage || 0),
    currentPercent: Number(book.currentPercent || 0),
    currentMinutes: Number(book.currentMinutes || 0),
  };
}

/**
 * Performs the book from recommendation operation.
 *
 * @param {*} recommendation - recommendation value.
 * @returns {*} The derived operation result.
 */
function bookFromRecommendation(recommendation) {
  const payload = recommendation?.actionPayload || {};
  return {
    catalogId: payload.catalogId || recommendation?.externalId || '',
    title: payload.title || recommendation?.title || '',
    subtitle: payload.subtitle || '',
    authors: Array.isArray(payload.authors) ? payload.authors : [],
    description: payload.description || recommendation?.body || '',
    coverUrl: payload.coverUrl || recommendation?.media?.url || '',
    publishedYear: payload.publishedYear || '',
    pageCount: Number(payload.pageCount || 0),
    genres: Array.isArray(payload.genres) ? payload.genres : [],
    shelf: defaultBook.shelf,
    collection: defaultBook.collection,
    format: defaultBook.format,
    favorite: false,
    rating: '',
    review: '',
    spoiler: false,
    notes: '',
    progressType: defaultBook.progressType,
    currentPage: 0,
    currentPercent: 0,
    currentMinutes: 0,
    startedDate: '',
    finishedDate: '',
    lastReadDate: '',
  };
}

/**
 * Performs the unique values operation.
 *
 * @param {Array<*>} items - items collection.
 * @param {*} getter - getter value.
 * @returns {*} The derived operation result.
 */
function uniqueValues(items, getter) {
  return Array.from(new Set(items.flatMap(getter).filter(Boolean))).sort();
}

/**
 * Performs the progress for operation.
 *
 * @param {*} book - book value.
 * @returns {*} The derived operation result.
 */
function progressFor(book) {
  if (book.progressType === 'Percent') {
    return Math.min(Number(book.currentPercent || 0), 100);
  }
  if (book.progressType === 'Minutes') {
    return 0;
  }
  if (!book.pageCount) return 0;
  return Math.min(Math.round((book.currentPage / book.pageCount) * 100), 100);
}

/**
 * Performs the progress label operation.
 *
 * @param {*} book - book value.
 * @returns {*} The derived operation result.
 */
function progressLabel(book) {
  if (book.progressType === 'Percent' && book.currentPercent) return `${book.currentPercent}%`;
  if (book.progressType === 'Minutes' && book.currentMinutes) return `${book.currentMinutes} min`;
  if (book.progressType === 'Pages' && book.currentPage) return book.pageCount ? `${book.currentPage}/${book.pageCount} pages` : `Page ${book.currentPage}`;
  return '';
}

/**
 * Performs the compact book context operation.
 *
 * @param {*} book - book value.
 * @returns {*} The derived operation result.
 */
function compactBookContext(book) {
  const progress = progressLabel(book);
  if (book.shelf === 'Reading' && progress) return [book.shelf, progress].filter(Boolean).join(' • ');
  if (book.shelf === 'Read') return [book.shelf, book.rating ? `★ ${book.rating}` : ''].filter(Boolean).join(' • ');
  if (book.shelf === 'Want to read') return [book.shelf, book.collection].filter(Boolean).join(' • ');
  return [book.shelf, book.collection || book.format || progress].filter(Boolean).join(' • ');
}

/** Owns the Reading library, search/import flows, progress, filters, and detail routing. */
export default function ReadingModule({
  isAdding,
  setIsAdding,
  theme,
  onClose,
  onRegisterCloseGuard,
  onHeaderActionsChange,
  onHeaderTitleChange,
  modulePath = '',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const targetItemId = searchParams.get('itemId');
  const openedTargetRef = useRef(null);
  const openedRecommendationRef = useRef(null);
  const [importedBook, setImportedBook] = useState(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [editingBook, setEditingBook] = useState(null);
  const [bookPendingDelete, setBookPendingDelete] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [search, setSearch] = useState('');
  const [activeShelf, setActiveShelf] = useState('');
  const [activeFormat, setActiveFormat] = useState('');
  const [activeGenre, setActiveGenre] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showGoodreadsImport, setShowGoodreadsImport] = useState(false);
  const [goodreadsImportResult, setGoodreadsImportResult] = useState(null);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const routeParts = useMemo(
    () => String(modulePath || '').split('/').map((part) => decodeURIComponent(part)).filter(Boolean),
    [modulePath]
  );
  const isCreateRoute = routeParts[0] === 'new';
  const routeBookId = routeParts[0] && routeParts[0] !== 'new' ? routeParts[0] : '';
  const isEditRoute = Boolean(routeBookId && routeParts[1] === 'edit');
  const isAddFlow = isAdding || isCreateRoute;
  const subscriptionRefetch = [{ query: GET_MY_SUBSCRIPTION_STATUS }];
  const { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError, refetch: refetchSubscription } = useQuery(GET_MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const subscription = subscriptionData?.getMySubscriptionStatus;
  const importUsageReady = !subscriptionLoading && !subscriptionError && Boolean(subscription)
    && (hasPremiumForHobby(subscription, 'reading') || getHobbyUsageLimit(subscription, 'reading')?.remaining != null);

  const { loading, error, data, fetchMore } = useQuery(GET_BOOKS, {
    variables: { first: BOOKS_PAGE_SIZE, after: null },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const [searchBooks, { data: searchData, loading: isSearching, error: searchError }] = useLazyQuery(SEARCH_BOOKS);
  const [loadBookDetail] = useLazyQuery(GET_BOOK, { fetchPolicy: 'network-only' });
  const parsedBooks = useMemo(() => (data?.getBooksListPage?.nodes || []).map(parseBook), [data]);
  const booksPageInfo = data?.getBooksListPage?.pageInfo || {};

  const shelves = useMemo(() => uniqueValues(parsedBooks, (book) => [book.shelf]), [parsedBooks]);
  const collections = useMemo(() => uniqueValues(parsedBooks, (book) => [book.collection]), [parsedBooks]);
  const formats = useMemo(() => uniqueValues(parsedBooks, (book) => [book.format]), [parsedBooks]);
  const genres = useMemo(() => uniqueValues(parsedBooks, (book) => book.genres), [parsedBooks]);

  const filteredBooks = useMemo(() => {
    const query = search.trim().toLowerCase();
    return parsedBooks.filter((book) => {
      const searchable = [
        book.title,
        book.subtitle,
        book.authors.join(' '),
        book.description,
        book.collection,
        book.shelf,
        book.format,
        book.genres.join(' '),
        book.review,
      ].join(' ').toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!activeShelf || book.shelf === activeShelf) &&
        (!activeFormat || book.format === activeFormat) &&
        (!activeGenre || book.genres.includes(activeGenre)) &&
        (!favoritesOnly || book.favorite)
      );
    });
  }, [activeFormat, activeGenre, activeShelf, favoritesOnly, parsedBooks, search]);

  const [addBook, { loading: isSaving }] = useMutation(ADD_BOOK, {
    refetchQueries: [{ query: GET_BOOKS, variables: { first: BOOKS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setImportedBook(null);
      setManualEntry(false);
      setHasFormChanges(false);
      onClose();
    },
  });

  const [updateBook, { loading: isUpdating }] = useMutation(UPDATE_BOOK, {
    refetchQueries: [{ query: GET_BOOKS, variables: { first: BOOKS_PAGE_SIZE, after: null } }],
    onCompleted: (result) => {
      const updated = parseBook(result.updateBook);
      setEditingBook(null);
      setHasFormChanges(false);
      setSelectedBook(updated);
    },
  });

  const [deleteBook, { loading: isDeleting }] = useMutation(DELETE_BOOK, {
    refetchQueries: [{ query: GET_BOOKS, variables: { first: BOOKS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setBookPendingDelete(null);
      setSelectedBook(null);
      navigate('/hobby/reading');
    },
  });

  const [previewGoodreadsImport, {
    data: goodreadsPreviewData,
    loading: isPreviewingGoodreads,
    error: goodreadsPreviewError,
    reset: resetGoodreadsPreview,
  }] = useMutation(PREVIEW_GOODREADS_IMPORT);
  const [importGoodreadsBooks, {
    loading: isImportingGoodreads,
    error: goodreadsImportError,
    reset: resetGoodreadsImport,
  }] = useMutation(IMPORT_GOODREADS_BOOKS, {
    refetchQueries: [{ query: GET_BOOKS, variables: { first: BOOKS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: (result) => {
      setGoodreadsImportResult(result.importGoodreadsBooks);
    },
  });

  /**
   * Normalizes input.
   *
   * @param {*} formData - form data value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const cleanInput = (formData) => ({
    title: formData.title.trim(),
    subtitle: formData.subtitle.trim(),
    authors: splitCsv(formData.authors),
    description: formData.description.trim(),
    coverUrl: formData.coverUrl.trim(),
    publishedYear: formData.publishedYear.trim(),
    pageCount: Number(formData.pageCount || 0),
    genres: splitCsv(formData.genres),
    shelf: formData.shelf,
    collection: formData.collection.trim() || defaultBook.collection,
    format: formData.format,
    favorite: Boolean(formData.favorite),
    rating: formData.rating,
    review: formData.review.trim(),
    spoiler: Boolean(formData.spoiler),
    notes: '',
    progressType: formData.progressType,
    currentPage: Number(formData.currentPage || 0),
    currentPercent: Number(formData.currentPercent || 0),
    currentMinutes: Number(formData.currentMinutes || 0),
    startedDate: formData.startedDate,
    finishedDate: formData.finishedDate,
    lastReadDate: formData.lastReadDate,
  });

  /**
   * Handles save.
   *
   * @param {*} formData - form data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (formData) => {
    const input = cleanInput(formData);
    try {
      if (formData.id) {
        await updateBook({ variables: { id: formData.id, input } });
        navigate(`/hobby/reading/${encodeURIComponent(formData.id)}`);
        return;
      }
      await addBook({ variables: { input } });
      navigate('/hobby/reading');
    } catch (error) {
      if (isLimitReachedError(error)) {
        setShowPremiumUpsell(true);
        return;
      }
      alert(getPublicErrorMessage(error));
    }
  };

  const closeForm = useCallback(() => {
    const editingBookId = editingBook?.id;
    setImportedBook(null);
    setManualEntry(false);
    setEditingBook(null);
    setHasFormChanges(false);
    navigate(editingBookId ? `/hobby/reading/${encodeURIComponent(editingBookId)}` : '/hobby/reading');
    onClose();
  }, [editingBook?.id, navigate, onClose]);

  const handleCancelForm = useCallback(({ confirmDiscard } = {}) => {
    if (confirmDiscard && hasFormChanges) {
      const shouldDiscard = window.confirm('Discard your unsaved book changes?');
      if (!shouldDiscard) return;
    }
    closeForm();
  }, [closeForm, hasFormChanges]);

  const handleSearch = useCallback((query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    searchBooks({ variables: { query: trimmedQuery } });
  }, [searchBooks]);

  /**
   * Handles use book.
   *
   * @param {*} book - book value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleUseBook = (book) => {
    if (!canCreateEntries(subscription, 'reading')) {
      setShowPremiumUpsell(true);
      return;
    }
    setImportedBook({
      title: book.title,
      subtitle: book.subtitle || '',
      authors: book.authors || [],
      description: book.description || '',
      coverUrl: book.coverUrl || '',
      publishedYear: book.publishedYear || '',
      pageCount: book.pageCount || 0,
      genres: book.genres || [],
      shelf: 'Want to read',
      collection: 'My Books',
      format: 'Physical',
      favorite: false,
      rating: '',
      review: '',
      spoiler: false,
      notes: '',
      progressType: 'Pages',
      currentPage: 0,
      currentPercent: 0,
      currentMinutes: 0,
      startedDate: '',
      finishedDate: '',
      lastReadDate: '',
    });
    setManualEntry(false);
    setIsAdding(true);
    navigate('/hobby/reading/new');
  };

  /**
   * Handles start add.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartAdd = () => {
    if (!canCreateEntries(subscription, 'reading')) {
      setShowPremiumUpsell(true);
      return;
    }
    setImportedBook(null);
    setManualEntry(false);
    setIsAdding(true);
    navigate('/hobby/reading/new');
  };

  /**
   * Handles preview goodreads.
   *
   * @param {*} csvText - csv text value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handlePreviewGoodreads = async (csvText) => {
    setGoodreadsImportResult(null);
    const result = await previewGoodreadsImport({ variables: { input: { csvText } } });
    return result.data?.previewGoodreadsImport;
  };

  /**
   * Performs the reset goodreads flow operation.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const resetGoodreadsFlow = () => {
    setGoodreadsImportResult(null);
    resetGoodreadsPreview();
    resetGoodreadsImport();
  };

  /**
   * Handles open goodreads import.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleOpenGoodreadsImport = () => {
    resetGoodreadsFlow();
    setShowGoodreadsImport(true);
    void refetchSubscription().catch(() => {});
  };

  useEffect(() => {
    if (isAddFlow || editingBook) {
      onHeaderActionsChange?.(null);
      return () => onHeaderActionsChange?.(null);
    }

    if (selectedBook) {
      onHeaderActionsChange?.(
        <DetailActionMenu
          label="Book actions"
          primaryAction={{ label: 'Edit', onSelect: () => handleEditBook(selectedBook) }}
          actions={[
            { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('reading', selectedBook)) },
            { label: mediaStrings.reading.deleteAction, variant: 'danger', onSelect: () => setBookPendingDelete(selectedBook) },
          ]}
        />
      );
      return () => onHeaderActionsChange?.(null);
    }

    onHeaderActionsChange?.(
      <div className="recipe-actions">
        <button className="btn-secondary" type="button" onClick={handleOpenGoodreadsImport}>{mediaStrings.reading.importAction}</button>
        <button className="btn-primary" type="button" onClick={handleStartAdd}>{mediaStrings.reading.addAction}</button>
      </div>
    );
    return () => onHeaderActionsChange?.(null);
  }, [editingBook, isAddFlow, onHeaderActionsChange, selectedBook, subscription]);

  useEffect(() => {
    if (isEditRoute || editingBook) {
      onHeaderTitleChange?.(mediaStrings.reading.form.editTitle);
    } else if (isCreateRoute || isAdding) {
      onHeaderTitleChange?.(mediaStrings.reading.addAction);
    } else if (routeBookId || selectedBook) {
      onHeaderTitleChange?.(selectedBook?.title || 'Book');
    } else {
      onHeaderTitleChange?.('');
    }

    return () => onHeaderTitleChange?.('');
  }, [editingBook, isAdding, isCreateRoute, isEditRoute, onHeaderTitleChange, routeBookId, selectedBook]);

  /**
   * Handles close goodreads import.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleCloseGoodreadsImport = () => {
    setShowGoodreadsImport(false);
    resetGoodreadsFlow();
  };

  /**
   * Handles import goodreads.
   *
   * @param {object} options - Behavioral options for the operation.
   * @param {*} options.csvText - csv text value.
   * @param {*} options.selectedRowIds - selected row ids value.
   * @param {*} options.collection - collection value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleImportGoodreads = async ({ csvText, selectedRowIds, collection }) => {
    if (!canCreateEntries(subscription, 'reading', selectedRowIds?.length || 1)) {
      setShowPremiumUpsell(true);
      return false;
    }
    try {
      const result = await importGoodreadsBooks({ variables: { input: { csvText, selectedRowIds, collection } } });
      return result.data?.importGoodreadsBooks;
    } catch (error) {
      if (isLimitReachedError(error)) {
        setShowPremiumUpsell(true);
        return false;
      }
      throw error;
    }
  };

  /**
   * Handles confirm delete.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleConfirmDelete = async () => {
    if (!bookPendingDelete) return;
    await deleteBook({ variables: { id: bookPendingDelete.id } });
  };

  /**
   * Handles load more.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMore = async () => {
    if (!booksPageInfo.hasNextPage || !booksPageInfo.endCursor) return;
    await fetchMore({
      variables: {
        first: BOOKS_PAGE_SIZE,
        after: booksPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getBooksListPage) return previousResult;
        return {
          getBooksListPage: {
            ...fetchMoreResult.getBooksListPage,
            nodes: [
              ...(previousResult.getBooksListPage?.nodes || []),
              ...(fetchMoreResult.getBooksListPage.nodes || []),
            ],
          },
        };
      },
    });
  };

  /**
   * Renders premium upsell.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderPremiumUpsell = () => showPremiumUpsell && (
    <PremiumUpsellModal
      hobbyId="reading"
      subscription={subscription}
      onClose={() => setShowPremiumUpsell(false)}
      onOpenAccount={() => {
        setShowPremiumUpsell(false);
        window.location.assign('/account');
      }}
    />
  );

  const fetchBookDetail = useCallback(async (book) => {
    const result = await loadBookDetail({ variables: { id: book.id } });
    return parseBook(result.data?.getBook || book);
  }, [loadBookDetail]);

  /**
   * Handles select book.
   *
   * @param {*} book - book value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSelectBook = async (book) => {
    setSelectedBook(book);
    navigate(`/hobby/reading/${encodeURIComponent(book.id)}`);
  };

  useEffect(() => {
    if (isCreateRoute) {
      setSelectedBook(null);
      setEditingBook(null);
      setIsAdding(true);
      const recommendation = location.state?.homeRecommendation;
      const recommendationKey = recommendation?.id || `${recommendation?.provider || ''}:${recommendation?.externalId || ''}:${recommendation?.title || ''}`;
      if (recommendation?.actionType === 'add_book_to_reading_list' && openedRecommendationRef.current !== recommendationKey) {
        openedRecommendationRef.current = recommendationKey;
        if (!canCreateEntries(subscription, 'reading')) {
          setShowPremiumUpsell(true);
          navigate('/hobby/reading/new', { replace: true, state: null });
          return;
        }
        setImportedBook(bookFromRecommendation(recommendation));
        setManualEntry(false);
        navigate('/hobby/reading/new', { replace: true, state: null });
      }
      return;
    }

    if (!routeBookId) {
      setSelectedBook(null);
      setEditingBook(null);
      setImportedBook(null);
      setManualEntry(false);
      setIsAdding(false);
      return;
    }

    setIsAdding(false);
    const book = parsedBooks.find((item) => item.id === routeBookId) || { id: routeBookId };
    fetchBookDetail(book)
      .then((detail) => {
        if (isEditRoute) {
          setSelectedBook(null);
          setEditingBook(detail);
          return;
        }
        setEditingBook(null);
        setSelectedBook(detail);
      })
      .catch(() => navigate('/hobby/reading', { replace: true }));
  }, [fetchBookDetail, isCreateRoute, isEditRoute, location.state, navigate, parsedBooks, routeBookId, setIsAdding, subscription]);

  /**
   * Handles edit book.
   *
   * @param {*} book - book value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleEditBook = async (book) => {
    navigate(`/hobby/reading/${encodeURIComponent(book.id)}/edit`);
    setEditingBook(await fetchBookDetail(book));
  };

  useEffect(() => {
    if (!targetItemId || openedTargetRef.current === targetItemId) return;
    openedTargetRef.current = targetItemId;
    navigate(`/hobby/reading/${encodeURIComponent(targetItemId)}`, { replace: true });
  }, [navigate, targetItemId]);

  useEffect(() => {
    if (isAddFlow || editingBook) {
      onRegisterCloseGuard?.(() => handleCancelForm({ confirmDiscard: true }));
      return () => onRegisterCloseGuard?.(null);
    }
    if (selectedBook) {
      onRegisterCloseGuard?.(() => navigate('/hobby/reading'));
      return () => onRegisterCloseGuard?.(null);
    }
    onRegisterCloseGuard?.(null);
    return undefined;
  }, [editingBook, handleCancelForm, isAddFlow, navigate, onRegisterCloseGuard, selectedBook]);

  if (isAddFlow && !editingBook && !importedBook && !manualEntry) {
    return (
      <ReadingAddSearch
        results={searchData?.searchBooks || []}
        onCancel={handleCancelForm}
        onManualEntry={() => {
          if (!canCreateEntries(subscription, 'reading')) {
            setShowPremiumUpsell(true);
            return;
          }
          setManualEntry(true);
        }}
        onSearch={handleSearch}
        onUseBook={handleUseBook}
        isSearching={isSearching}
        error={searchError}
      />
    );
  }

  if (isAddFlow || editingBook) {
    return (
      <>
        <ReadingForm
          theme={theme}
          onCancel={handleCancelForm}
          onSave={handleSave}
          isSaving={isSaving || isUpdating}
          initialData={editingBook || importedBook}
          onDirtyChange={setHasFormChanges}
          collections={collections}
          isEditing={Boolean(editingBook)}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (selectedBook) {
    const progress = progressFor(selectedBook);
    const heroFacts = [
      [mediaStrings.reading.form.fields.shelf, selectedBook.shelf],
      [mediaStrings.reading.form.fields.progressType, progressLabel(selectedBook)],
      [mediaStrings.reading.form.fields.rating, selectedBook.rating ? <RatingReadout rating={selectedBook.rating} /> : ''],
    ];
    const libraryRows = [
      [mediaStrings.reading.form.fields.shelf, selectedBook.shelf],
      [mediaStrings.reading.form.fields.collection, selectedBook.collection],
      [mediaStrings.reading.form.fields.format, selectedBook.format],
      [mediaStrings.reading.form.fields.favorite, selectedBook.favorite ? mediaStrings.library.favorites : ''],
    ];
    const progressRows = [
      [mediaStrings.reading.form.fields.progressType, selectedBook.progressType],
      [mediaStrings.reading.progressField.page, selectedBook.currentPage ? (selectedBook.pageCount ? `${selectedBook.currentPage}/${selectedBook.pageCount} pages` : `Page ${selectedBook.currentPage}`) : ''],
      [mediaStrings.reading.progressField.percent, selectedBook.currentPercent ? `${selectedBook.currentPercent}%` : ''],
      [mediaStrings.reading.progressField.minutes, selectedBook.currentMinutes ? `${selectedBook.currentMinutes} min` : ''],
      [mediaStrings.reading.form.fields.started, selectedBook.startedDate],
      [mediaStrings.reading.form.fields.finished, selectedBook.finishedDate],
    ];
    const detailRows = [
      [mediaStrings.reading.form.fields.subtitle, selectedBook.subtitle],
      [mediaStrings.reading.form.fields.authors, selectedBook.authors.length ? selectedBook.authors.join(', ') : ''],
      [mediaStrings.reading.form.fields.publishedYear, selectedBook.publishedYear],
      [mediaStrings.reading.form.fields.pageCount, selectedBook.pageCount ? `${selectedBook.pageCount} pages` : ''],
    ];
    return (
      <div className="module-wrapper reading-module">
        <div className="recipe-detail-view">
          <button type="button" className="recipe-back-link" onClick={() => navigate('/hobby/reading')}>← Back</button>

          {bookPendingDelete && (
            <div className="modal-overlay">
              <div className="modal-content delete-confirmation">
                <p className="eyebrow">{mediaStrings.reading.deleteTitle}</p>
                <h2>{mediaStrings.reading.deletePrompt}</h2>
                <p>{mediaStrings.reading.deleteDetail(bookPendingDelete.title)}</p>
                <div className="form-actions">
                  <button type="button" className="btn-ghost" onClick={() => setBookPendingDelete(null)}>{mediaStrings.library.cancel}</button>
                  <button type="button" className="btn-danger solid" onClick={handleConfirmDelete} disabled={isDeleting}>
                    {isDeleting ? mediaStrings.library.deleting : mediaStrings.reading.deleteAction}
                  </button>
                </div>
              </div>
            </div>
          )}

          <section className="recipe-detail-hero movie-detail-hero">
            {selectedBook.coverUrl ? (
              <img className="movie-detail-poster book-detail-cover" src={selectedBook.coverUrl} alt="" />
            ) : (
              <DefaultMediaFallback className="entry-detail-photo" fallbackType="reading" variant="detail-square" />
            )}
            <div className="movie-detail-copy">
              <p className="eyebrow">{selectedBook.shelf}</p>
              <h2>{selectedBook.title}</h2>
              <p>{[selectedBook.authors.join(', '), selectedBook.publishedYear].filter(Boolean).join(' - ')}</p>
              {selectedBook.description && <p>{selectedBook.description}</p>}
            </div>
            {heroFacts.filter(([, value]) => value).length > 0 && (
              <div className="recipe-detail-facts">
                {heroFacts.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}>
                    <small>{label}</small>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="recipe-detail-grid">
            <section className="recipe-detail-panel">
              <h3>{mediaStrings.reading.form.sections.library}</h3>
              <dl className="movie-info-list">
                {libraryRows.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                ))}
              </dl>
            </section>
            <section className="recipe-detail-panel">
              <h3>{mediaStrings.reading.progressTitle}</h3>
              {progress > 0 && <div className="tv-progress-track"><span style={{ width: `${progress}%` }} /></div>}
              <dl className="movie-info-list">
                {progressRows.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                ))}
              </dl>
            </section>
          </div>

          <div className="recipe-detail-grid">
            <section className="recipe-detail-panel">
              <h3>{mediaStrings.reading.detailsTitle}</h3>
              <dl className="movie-info-list">
                {detailRows.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                ))}
              </dl>
            </section>
            {selectedBook.genres.length > 0 && (
              <section className="recipe-detail-panel">
                <h3>{mediaStrings.reading.form.fields.genres}</h3>
                <div className="compact-tags">
                  {selectedBook.genres.map((genre) => <span key={genre}>{genre}</span>)}
                </div>
              </section>
            )}
          </div>

          <section className="recipe-detail-panel">
            <h3>{selectedBook.spoiler ? mediaStrings.library.spoilers : mediaStrings.reading.reviewTitle}</h3>
            {selectedBook.review && <p className="detail-notes">{selectedBook.review}</p>}
            {!selectedBook.review && <p className="detail-notes">{mediaStrings.library.noReviewOrNotes}</p>}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="module-wrapper reading-module movies-module">
      {renderPremiumUpsell()}
      {showGoodreadsImport && (
        <GoodreadsImportModal
          remainingEntries={getRemainingEntries(subscription, 'reading')}
          usageReady={importUsageReady}
          usageError={!subscriptionLoading && !importUsageReady}
          onRetryUsage={() => { void refetchSubscription().catch(() => {}); }}
          onUpgrade={() => { setShowGoodreadsImport(false); setShowPremiumUpsell(true); }}
          error={isLimitReachedError(goodreadsPreviewError || goodreadsImportError) ? null : goodreadsPreviewError || goodreadsImportError}
          importResult={goodreadsImportResult}
          isImporting={isImportingGoodreads}
          isPreviewing={isPreviewingGoodreads}
          onClose={handleCloseGoodreadsImport}
          onImport={handleImportGoodreads}
          onPreview={handlePreviewGoodreads}
          preview={goodreadsPreviewData?.previewGoodreadsImport}
          collections={collections}
        />
      )}
      <div className="recipe-library-layout">
        <aside className="recipe-books-panel">
          <p className="eyebrow">{mediaStrings.library.shelves}</p>
          <div className="recipe-book-list">
            <button type="button" className={!activeShelf ? 'active' : ''} onClick={() => setActiveShelf('')}>
              <span>{mediaStrings.reading.allItems}</span>
              <strong>{parsedBooks.length}</strong>
            </button>
            {shelves.map((shelf) => (
              <button key={shelf} type="button" className={activeShelf === shelf ? 'active' : ''} onClick={() => setActiveShelf(shelf)}>
                <span>{shelf}</span>
                <strong>{parsedBooks.filter((book) => book.shelf === shelf).length}</strong>
              </button>
            ))}
          </div>
        </aside>

        <section className="recipe-main-panel">
          <div className="recipe-filters">
            <div className="form-group search-field">
              <label>{mediaStrings.reading.addSearch.label}</label>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={mediaStrings.reading.searchPlaceholder} />
            </div>

            <div className="filter-row movie-filter-row">
              <label>
                {mediaStrings.library.format}
                <select value={activeFormat} onChange={(event) => setActiveFormat(event.target.value)}>
                  <option value="">{mediaStrings.library.anyFormat}</option>
                  {formats.map((format) => <option key={format} value={format}>{format}</option>)}
                </select>
              </label>
              <label>
                {mediaStrings.library.genre}
                <select value={activeGenre} onChange={(event) => setActiveGenre(event.target.value)}>
                  <option value="">{mediaStrings.library.anyGenre}</option>
                  {genres.map((genre) => <option key={genre} value={genre}>{genre}</option>)}
                </select>
              </label>
              <button type="button" className={`favorite-filter ${favoritesOnly ? 'active' : ''}`} onClick={() => setFavoritesOnly((current) => !current)}>
                {mediaStrings.library.favorites}
              </button>
            </div>
          </div>

          {loading && parsedBooks.length === 0 && (
            <div className="module-loading-state">
              <ModuleListSkeleton />
            </div>
          )}
          {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

          <div className="recipe-stats">
            <span>{mediaStrings.library.shown(filteredBooks.length)}</span>
            <span>{mediaStrings.library.favoritesCount(parsedBooks.filter((book) => book.favorite).length)}</span>
            <span>{mediaStrings.library.activeCount(parsedBooks.filter((book) => book.shelf === 'Reading').length)}</span>
          </div>

          <div className="recipe-card-grid">
            {filteredBooks.map((book) => (
              <button key={book.id} type="button" className="recipe-card movie-card" onClick={() => handleSelectBook(book)}>
                <div className="movie-card-title">
                  {book.coverUrl ? (
                    <img className="movie-poster-thumb" src={book.coverUrl} alt="" />
                  ) : (
                    <DefaultMediaFallback className="entry-card-photo" fallbackType="reading" variant="thumbnail-square" />
                  )}
                  <div>
                    <div className="movie-card-heading">
                      <h3>{book.title}</h3>
                      {book.favorite && <span className="favorite-chip">{mediaStrings.library.favorites}</span>}
                    </div>
                    <p>{[book.authors.join(', '), book.publishedYear].filter(Boolean).join(' • ') || mediaStrings.reading.detailsMissing}</p>
                    <p className="movie-card-detail-line">{compactBookContext(book) || mediaStrings.library.libraryDetailsMissing}</p>
                  </div>
                </div>
                {book.rating && <span className="movie-rating-pill" aria-label={mediaStrings.library.ratingLabel(book.rating)}>★ {book.rating}</span>}
              </button>
            ))}
          </div>

          {booksPageInfo.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading books...' : 'Load more books'}
              </button>
            </div>
          )}

          {filteredBooks.length === 0 && !loading && (
            <HobbyEmptyState hobbyId="reading" filtered={parsedBooks.length > 0}
              title={parsedBooks.length === 0 ? mediaStrings.reading.emptyTitle : moduleCommonStrings.empty.noMatches}
              detail={parsedBooks.length === 0 ? mediaStrings.reading.emptyFirst : moduleCommonStrings.filters.tryDifferentSearchOrFilter} />
          )}
        </section>
      </div>
    </div>
  );
}
